/**
 * Minimal API client for ContinuumCX FastAPI backend.
 *
 * - Uses fetch with credentials: 'include' so cookies (cx_access, cx_refresh)
 *   round-trip automatically. The backend sets them httpOnly, Secure, SameSite=Lax.
 * - On 401, attempts a single refresh, retries the original call, then surfaces
 *   the failure if refresh fails (caller redirects to /login).
 * - All paths are relative to /api/ — nginx proxies to FastAPI.
 */

const BASE = '/api';

let _refreshInflight = null;

// Per-TAB softphone session id. Minted once when this JS module loads (i.e.
// once per tab/window). Sent on every API call as X-CX-Softphone-Session and
// on the SSE connect as ?sid=. The backend enforces a single ACTIVE session
// per agent (last SSE connect wins) and rejects /state posts from a superseded
// session — fixing the multi-tab storm where background tabs reverted the
// active tab's ACD state (2026-07-29).
export const SOFTPHONE_SESSION_ID = (() => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* ignore */ }
  return 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
})();

export function getSoftphoneSessionId() {
  return SOFTPHONE_SESSION_ID;
}

async function rawFetch(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CX-Softphone-Session': SOFTPHONE_SESSION_ID,
      ...(opts.headers || {}),
    },
  });
  return res;
}

async function tryRefresh() {
  if (!_refreshInflight) {
    _refreshInflight = rawFetch('/auth/refresh', { method: 'POST' })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        // Clear after a short delay so concurrent callers share the same result
        setTimeout(() => { _refreshInflight = null; }, 50);
      });
  }
  return _refreshInflight;
}

export class ApiError extends Error {
  constructor(status, detail, body) {
    // Normalize whatever shape FastAPI returned into a human-readable string.
    // Common shapes:
    //   - plain string body: "some error"
    //   - {detail: "some error"} (FastAPI HTTPException with string)
    //   - {detail: [{loc:[...], msg:"...", type:"..."}, ...]} (Pydantic validation)
    //   - {detail: {...}} (custom dict)
    // Whatever we get, .message and .detail must end up as STRINGS so callers
    // that do `${e.detail}` never get `[object Object]`.
    const inner = detail?.detail ?? detail;
    const stringified = stringifyApiError(inner, status);
    super(stringified);
    this.status = status;
    this.detail = stringified;
    this.body = body;
    this.raw = inner;
  }
}

function stringifyApiError(value, status) {
  if (value == null) return `HTTP ${status}`;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    // Pydantic validation array
    return value
      .map((v) => {
        if (!v) return '';
        if (typeof v === 'string') return v;
        const loc = Array.isArray(v.loc) ? v.loc.filter((p) => p !== 'body').join('.') : '';
        const msg = v.msg || v.message || JSON.stringify(v);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join('; ') || `HTTP ${status}`;
  }
  if (typeof value === 'object') {
    // Generic dict — try common fields first
    return value.msg || value.message || value.error || JSON.stringify(value);
  }
  return String(value);
}

export async function api(path, opts = {}, { retryOn401 = true } = {}) {
  let res = await rawFetch(path, opts);

  if (res.status === 401 && retryOn401 && !path.startsWith('/auth/login') && !path.startsWith('/auth/refresh')) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await rawFetch(path, opts);
    }
  }

  let body = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    body = await res.json().catch(() => null);
  } else {
    body = await res.text().catch(() => null);
  }

  if (!res.ok) {
    throw new ApiError(res.status, body, body);
  }
  return body;
}

// --- Convenience wrappers -----------------------------------------------

export const authApi = {
  login:           (email, password) =>
    api('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }, { retryOn401: false }),
  passwordReset:   (new_password) =>
    api('/auth/password/reset', { method: 'POST', body: JSON.stringify({ new_password }) }, { retryOn401: false }),
  mfaEnrollStart:  () =>
    api('/auth/mfa/enroll/start', { method: 'POST' }, { retryOn401: false }),
  mfaEnrollVerify: (code) =>
    api('/auth/mfa/enroll/verify', { method: 'POST', body: JSON.stringify({ code }) }, { retryOn401: false }),
  mfaVerify:       (code) =>
    api('/auth/mfa/verify', { method: 'POST', body: JSON.stringify({ code }) }, { retryOn401: false }),
  refresh:         () =>
    api('/auth/refresh', { method: 'POST' }, { retryOn401: false }),
  logout:          () =>
    api('/auth/logout', { method: 'POST' }, { retryOn401: false }),
  me:              () =>
    api('/auth/me'),
};

export const healthApi = {
  ready: () => api('/health/ready', {}, { retryOn401: false }),
};

export const agentsApi = {
  list:    (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return api(`/admin/agents/${q ? '?' + q : ''}`);
  },
  get:     (id)        => api(`/admin/agents/${id}`),
  create:  (payload)   => api('/admin/agents/', { method: 'POST', body: JSON.stringify(payload) }),
  update:  (id, patch) => api(`/admin/agents/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove:  (id, { hard = false } = {}) =>
    api(`/admin/agents/${id}${hard ? '?hard=true' : ''}`, { method: 'DELETE' }),
  importDryRun: (file) => uploadCsv('/admin/agents/import/dry-run', file),
  importCommit: (file) => uploadCsv('/admin/agents/import/commit', file),
  sync:         (id) => api(`/admin/agents/${id}/sync`, { method: 'POST' }),
};

async function uploadCsv(path, file) {
  const form = new FormData();
  form.append('file', file);
  // Don't set Content-Type — browser adds the multipart boundary automatically.
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, body, body);
  return body;
}

export const softphoneApi = {
  credentials: () => api('/softphone/credentials', { method: 'POST' }),
  setState: (state, reason) =>
    api('/softphone/state', { method: 'POST', body: JSON.stringify({ state, reason: reason || null }) }),
  dialPrecheck: (destination) =>
    api('/softphone/dial-precheck', { method: 'POST', body: JSON.stringify({ destination }) }),
  conferenceMerge: () =>
    api('/softphone/conference/merge', { method: 'POST', body: JSON.stringify({}) }),
  conferenceMergeHeld: () =>
    api('/softphone/conference/merge-held', { method: 'POST', body: JSON.stringify({}) }),
  conferenceRelease: (room) =>
    api('/softphone/conference/release', { method: 'POST', body: JSON.stringify({ room: room || null }) }),
  // 2026-07-23: server-side Hold (bypasses SIP re-INVITE hold to avoid
  // rtpengine ICE regeneration bug). Frontend still needs to mute + deafen
  // browser tracks locally to complete the UX.
  holdStart: () =>
    api('/softphone/hold/start', { method: 'POST', body: JSON.stringify({}) }),
  holdStop: () =>
    api('/softphone/hold/stop', { method: 'POST', body: JSON.stringify({}) }),
  // 2026-07-27: server-side multi-line. Agent keeps ONE browser leg; Asterisk
  // originates the 3rd party + bridges the agent, and swaps the agent's bridge
  // between caller and 3rd party. No browser 2nd call, no re-INVITE.
  lineAdd: (number) =>
    api('/softphone/line/add?number=' + encodeURIComponent(number), { method: 'POST', body: JSON.stringify({}) }),
  lineSwap: (to) =>
    api('/softphone/line/swap?to=' + encodeURIComponent(to), { method: 'POST', body: JSON.stringify({}) }),
  lineHangup: (which) =>
    api('/softphone/line/hangup?which=' + encodeURIComponent(which), { method: 'POST', body: JSON.stringify({}) }),
  lineMerge: () =>
    api('/softphone/line/merge', { method: 'POST', body: JSON.stringify({}) }),
  lineEndAll: () =>
    api('/softphone/line/end-all', { method: 'POST', body: JSON.stringify({}) }),
  // 2026-08-10: effective per-tenant AUX (Not-Ready reason) codes for the
  // current agent (Admin > AUX Codes). Fails open on the client side.
  auxCodes: () => api('/softphone/aux-codes'),
  callHistory: (hours) => api('/softphone/call-history' + (hours ? ('?hours=' + encodeURIComponent(hours)) : '')),
  // 2026-08-10: N-party expandable conference (Avaya-style, cap 4).
  // Add-Call FROM INSIDE a live conference; the conferenced parties keep
  // talking (no MOH while >=2 remain) while the agent steps out to dial.
  conferenceAddParty: (number) =>
    api('/softphone/conference/add-party?number=' + encodeURIComponent(number),
        { method: 'POST', body: JSON.stringify({}) }),
  // Merge the pending consult line INTO the existing conference room.
  conferenceMergeParty: () =>
    api('/softphone/conference/merge-party', { method: 'POST', body: JSON.stringify({}) }),
  // After Add-Call-from-conference: 'hold' the consult party on MOH or
  // 'hangup' the consult line, then return the agent to the conference.
  conferenceConsultReturn: (action) =>
    api('/softphone/conference/consult-return?action=' + encodeURIComponent(action),
        { method: 'POST', body: JSON.stringify({}) }),
  // 2026-10-12: presence heartbeat. The browser renews a short-lived presence
  // lease so the server can detect a real departure (tab close / sleep / Wi-Fi
  // drop) within ~1 min instead of a 16h janitor. Cheap: one Redis SET server-
  // side, no DB. PHASE 1 = shadow (server observes; no state change yet).
  presenceHeartbeat: () =>
    api('/softphone/presence', { method: 'POST', body: JSON.stringify({}) }),
  // Clean-close fast path — called on pagehide/beforeunload via sendBeacon so it
  // survives the tab closing. Best-effort; the lease TTL is the real backstop.
  presenceBye: () => {
    try {
      const sid = SOFTPHONE_SESSION_ID || '';
      const url = BASE + '/softphone/presence/bye?sid=' + encodeURIComponent(sid);
      if (navigator && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([JSON.stringify({ sid })], { type: 'application/json' });
        return navigator.sendBeacon(url, blob);
      }
      // Fallback: keepalive fetch (also survives unload in modern browsers).
      fetch(url, { method: 'POST', credentials: 'include', keepalive: true,
                   headers: { 'Content-Type': 'application/json',
                              'X-CX-Softphone-Session': sid },
                   body: JSON.stringify({ sid }) }).catch(() => {});
      return true;
    } catch (_) { return false; }
  },
};

// --- Outbound Dialing Profiles (2026-06-18) ---------------------------

export const dialingProfilesApi = {
  list:   ()         => api('/admin/dialing-profiles'),
  get:    (id)       => api(`/admin/dialing-profiles/${id}`),
  create: (payload)  => api('/admin/dialing-profiles', { method: 'POST', body: JSON.stringify(payload) }),
  update: (id, patch)=> api(`/admin/dialing-profiles/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id)       => api(`/admin/dialing-profiles/${id}`, { method: 'DELETE' }),
  resyncAll: ()      => api('/admin/dialing-profiles/resync-all', { method: 'POST' }),
  assignees: (id)    => api(`/admin/dialing-profiles/${id}/assignees`),
};

export const auxCodesApi = {
  // Admin: manage per-tenant AUX (Not-Ready reason) codes. `clientId`:
  // a tenant UUID, 'global' for the default set, or omit for ALL.
  list:    (clientId) => api('/admin/aux-codes'
              + (clientId !== undefined && clientId !== null
                 ? '?client_id=' + encodeURIComponent(clientId) : '')),
  create:  (payload)  => api('/admin/aux-codes', { method: 'POST', body: JSON.stringify(payload) }),
  update:  (id, patch)=> api(`/admin/aux-codes/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  remove:  (id)       => api(`/admin/aux-codes/${id}`, { method: 'DELETE' }),
  reorder: (orderedIds) => api('/admin/aux-codes/reorder', { method: 'POST', body: JSON.stringify({ ordered_ids: orderedIds }) }),
  // Agent: the effective AUX codes for the current user's tenant.
  mine:    ()         => api('/softphone/aux-codes'),
};

export const announcementsApi = {
  voices:     ()           => api('/admin/announcements/voices'),
  list:       (category)   => api(`/admin/announcements/${category ? '?category=' + category : ''}`),
  get:        (id)         => api(`/admin/announcements/${id}`),
  create:     (payload)    => api('/admin/announcements/', { method: 'POST', body: JSON.stringify(payload) }),
  update:     (id, patch)  => api(`/admin/announcements/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  regenerate: (id)         => api(`/admin/announcements/${id}/regenerate`, { method: 'POST' }),
  remove:     (id)         => api(`/admin/announcements/${id}`, { method: 'DELETE' }),
  previewUrl: (id)         => `/api/admin/announcements/${id}/preview`,
};

export const wallboardApi = {
  floor:    (clientId) => api('/wallboard/floor'  + (clientId ? `?client_id=${encodeURIComponent(clientId)}` : '')),
  queues:   (clientId) => api('/wallboard/queues' + (clientId ? `?client_id=${encodeURIComponent(clientId)}` : '')),
  agentState: (clientId) => api('/wallboard/agent-state' + (clientId ? `?client_id=${encodeURIComponent(clientId)}` : '')),
  personal: () => api('/wallboard/personal'),
  trunks:   (clientId, queueId) => {
    const p = new URLSearchParams();
    if (clientId) p.set('client_id', clientId);
    if (queueId) p.set('queue_id', queueId);
    const qs = p.toString();
    return api('/wallboard/trunks' + (qs ? `?${qs}` : ''));
  },
};

export const queuesApi = {
  list:   ()         => api('/admin/queues/'),
  get:    (id)       => api(`/admin/queues/${id}`),
  create: (payload)  => api('/admin/queues/', { method: 'POST', body: JSON.stringify(payload) }),
  update: (id, patch) => api(`/admin/queues/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id)       => api(`/admin/queues/${id}`, { method: 'DELETE' }),
  sync:   ()         => api('/admin/queues/sync', { method: 'POST' }),
};

// --- Phase 7: Permissions ----------------------------------------------

export const permissionsApi = {
  modules:        ()          => api('/permissions/modules'),
  me:             ()          => api('/permissions/me'),
  matrix:         ()          => api('/admin/permissions'),
  updateMatrix:   (matrix)    => api('/admin/permissions', { method: 'PUT', body: JSON.stringify({ matrix }) }),
  branding:       ()          => api('/me/branding'),
};

// --- Phase 8: Clients + Users ------------------------------------------

export const clientsApi = {
  list:        ()                 => api('/admin/clients'),
  create:      (payload)          => api('/admin/clients', { method: 'POST', body: JSON.stringify(payload) }),
  update:      (id, patch)        => api(`/admin/clients/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  remove:      (id)               => api(`/admin/clients/${id}`, { method: 'DELETE' }),
  uploadLogo:  (id, file)         => uploadFile(`/admin/clients/${id}/logo`, file),
  removeLogo:  (id)               => api(`/admin/clients/${id}/logo`, { method: 'DELETE' }),
  logoUrl:     (id)               => `/api/clients/${id}/logo`,
};

export const usersApi = {
  list:   (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return api(`/admin/users/${q ? '?' + q : ''}`);
  },
  create:        (payload)        => api('/admin/users/', { method: 'POST', body: JSON.stringify(payload) }),
  update:        (id, patch)      => api(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  setStatus:     (id, status)     => api(`/admin/users/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
  remove:        (id, { hard = false } = {}) => api(`/admin/users/${id}${hard ? '?hard=true' : ''}`, { method: 'DELETE' }),
  passwordReset: (id)             => api(`/admin/users/${id}/password-reset`, { method: 'POST' }),
  mfaReset:      (id)             => api(`/admin/users/${id}/mfa-reset`, { method: 'POST' }),
  importDryRun:  (file)           => uploadFile('/admin/users/import/dry-run', file),
  importCommit:  (file)           => uploadFile('/admin/users/import/commit', file),
};

async function uploadFile(path, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, body, body);
  return body;
}

// --- Phase 6: Recordings (admin + browser) -----------------------------

export const clustersApi = {
  list:     ()                     => api('/admin/clusters/'),
  health:   ()                     => api('/admin/clusters/health'),
  failover: (target, reason, force = false) =>
    api('/admin/clusters/failover', {
      method: 'POST',
      body: JSON.stringify({ target, reason, force }),
    }),
};

export const recordingAdminApi = {
  // Buckets
  bucketsList:    ()                 => api('/admin/recording/buckets'),
  bucketCreate:   (payload)          => api('/admin/recording/buckets', { method: 'POST', body: JSON.stringify(payload) }),
  bucketUpdate:   (id, patch)        => api(`/admin/recording/buckets/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  bucketDelete:   (id)               => api(`/admin/recording/buckets/${id}`, { method: 'DELETE' }),
  bucketTest:     (id)               => api(`/admin/recording/buckets/${id}/test`, { method: 'POST' }),

  // Retention
  retentionList:    ()                 => api('/admin/recording/retention'),
  retentionCreate:  (payload)          => api('/admin/recording/retention', { method: 'POST', body: JSON.stringify(payload) }),
  retentionUpdate:  (id, patch)        => api(`/admin/recording/retention/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  retentionDelete:  (id)               => api(`/admin/recording/retention/${id}`, { method: 'DELETE' }),

  // Archive rules
  rulesList:    ()                 => api('/admin/recording/archive-rules'),
  ruleCreate:   (payload)          => api('/admin/recording/archive-rules', { method: 'POST', body: JSON.stringify(payload) }),
  ruleUpdate:   (id, patch)        => api(`/admin/recording/archive-rules/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  ruleDelete:   (id)               => api(`/admin/recording/archive-rules/${id}`, { method: 'DELETE' }),

  // Worker triggers
  runIngest:    () => api('/admin/recording/ingest/run', { method: 'POST' }),
  runArchive:   () => api('/admin/recording/archive/run', { method: 'POST' }),
  runRetention: () => api('/admin/recording/retention/run', { method: 'POST' }),
  runEnrich:    () => api('/admin/recording/enrich/run', { method: 'POST' }),
};

// --- Phase 7: DID / TFN inventory --------------------------------------

export const didsApi = {
  list:        ()                       => api('/admin/dids/'),
  get:         (id)                     => api(`/admin/dids/${id}`),
  create:      (payload)                => api('/admin/dids/', { method: 'POST', body: JSON.stringify(payload) }),
  update:      (id, patch)              => api(`/admin/dids/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove:      (id)                     => api(`/admin/dids/${id}`, { method: 'DELETE' }),
  sync:        ()                       => api('/admin/dids/sync', { method: 'POST' }),
  importDryRun:(file)                   => uploadFile('/admin/dids/import/dry-run', file),
  importCommit:(file)                   => uploadFile('/admin/dids/import/commit', file),
};

// --- Phase 5b: Supervisor Tools ----------------------------------------

export const supervisorApi = {
  agents:        (clientId)                  => api('/supervisor/agents' + (clientId ? `?client_id=${encodeURIComponent(clientId)}` : '')),
  spy:           (ext)                       => api('/supervisor/spy',          { method: 'POST', body: JSON.stringify({ target_extension: ext }) }),
  whisper:       (ext)                       => api('/supervisor/whisper',      { method: 'POST', body: JSON.stringify({ target_extension: ext }) }),
  barge:         (ext)                       => api('/supervisor/barge',        { method: 'POST', body: JSON.stringify({ target_extension: ext }) }),
  forcePause:    (ext, reason)               => api('/supervisor/force-pause',  { method: 'POST', body: JSON.stringify({ target_extension: ext, reason }) }),
  forceLogout:   (ext, reason)               => api('/supervisor/force-logout', { method: 'POST', body: JSON.stringify({ target_extension: ext, reason }) }),
  hardReset:     (ext, reason)               => api('/supervisor/hard-reset',   { method: 'POST', body: JSON.stringify({ target_extension: ext, reason }) }),
  broadcasts:    ()                          => api('/supervisor/broadcasts'),
  broadcast:     (body)                      => api('/supervisor/broadcasts',   { method: 'POST', body: JSON.stringify(body) }),
  delBroadcast:  (id)                        => api(`/supervisor/broadcasts/${id}`, { method: 'DELETE' }),
};

export const broadcastsApi = {
  mine:    ()    => api('/broadcasts/mine'),
  ack:     (id)  => api(`/broadcasts/${id}/ack`, { method: 'POST' }),
};

export const reportsApi = {
  health: () => api('/reports/health'),

  // helper to build a query string from range + extras
  _qs: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return q ? '?' + q : '';
  },

  agents:        (params) => api('/reports/agents' + reportsApi._qs(params)),
  agentsByInteraction: (params) => api('/reports/agents-by-interaction' + reportsApi._qs(params)),
  agentTs:       (ext, params) => api(`/reports/agents/${encodeURIComponent(ext)}/timeseries` + reportsApi._qs(params)),
  queues:        (params) => api('/reports/queues' + reportsApi._qs(params)),
  queueTs:       (qid, params) => api(`/reports/queues/${encodeURIComponent(qid)}/timeseries` + reportsApi._qs(params)),
  cdr:           (params) => api('/reports/cdr' + reportsApi._qs(params)),
  cdrTrace:      (linkedid) => api(`/reports/cdr/${encodeURIComponent(linkedid)}/trace`),
  interaction:   (id) => api(`/reports/interaction/${encodeURIComponent(id)}`),
  trunks:        (params) => api('/reports/trunks' + reportsApi._qs(params)),

  // 2026-07-09 additions
  filterOptions:    () => api('/reports/filters/options'),
  acdDaily:         (params) => api('/reports/acd/daily' + reportsApi._qs(params)),
  outboundCompliance:(params) => api('/reports/outbound-compliance' + reportsApi._qs(params)),
  storageAndArchive:(params) => api('/reports/storage-and-archive' + reportsApi._qs(params)),
  transfers:        (params) => api('/reports/transfers' + reportsApi._qs(params)),
  recordingCoverage:(params) => api('/reports/recording-coverage' + reportsApi._qs(params)),
  coverageGaps:     (params) => api('/reports/coverage-gaps' + reportsApi._qs(params)),
  qaAgents:         (params) => api('/reports/qa/agents' + reportsApi._qs(params)),
  qaForms:          (params) => api('/reports/qa/forms' + reportsApi._qs(params)),
  qaEvaluators:     (params) => api('/reports/qa/evaluators' + reportsApi._qs(params)),
  failoverTimeline: (params) => api('/reports/failover-timeline' + reportsApi._qs(params)),
  auxTime:          (params) => api('/reports/aux-time' + reportsApi._qs(params)),
  // 2026-08-10 agent state / AUX reports (from agent_state_history)
  agentStateSummary:(params) => api('/reports/agent-state-summary' + reportsApi._qs(params)),
  auxReasons:       (params) => api('/reports/aux-reasons' + reportsApi._qs(params)),
  agentTimeline:    (params) => api('/reports/agent-state-timeline' + reportsApi._qs(params)),

  // file download helpers (browser will set Content-Disposition)
  downloadUrl:   (report, params, format) =>
    `/api/reports/${report}` + reportsApi._qs({ ...params, format }),
};

export const recordingsApi = {
  list: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return api(`/recordings/${q ? '?' + q : ''}`);
  },
  get:         (id) => api(`/recordings/${id}`),
  myQueues:    ()   => api('/recordings/my-queues'),
  myFilters:   ()   => api('/recordings/my-filters'),
  streamUrl:   (id) => `/api/recordings/${id}/stream`,
  downloadUrl: (id) => `/api/recordings/${id}/download`,
};

// Screen-recording API (Phase 2, 2026-06-22).
//
// Browser-driven: the agent's browser captures the screen with
// getDisplayMedia + MediaRecorder, then PUTs WebM block chunks directly
// to Azure Blob via the SAS URL returned by /begin. The CX backend never
// touches the bytes — it only brokers SAS URLs and tracks state.
export const screenRecordingsApi = {
  shouldRecord: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return api(`/screen-recordings/should-record${q ? '?' + q : ''}`);
  },
  begin: (payload) =>
    api('/screen-recordings/begin', {
      method: 'POST', body: JSON.stringify(payload),
    }),
  heartbeat: (id, payload) =>
    api(`/screen-recordings/${id}/heartbeat`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
  end: (id, payload) =>
    api(`/screen-recordings/${id}/end`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
  abort: (id, payload = {}) =>
    api(`/screen-recordings/${id}/abort`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
  reissueSas: (id) =>
    api(`/screen-recordings/${id}/reissue-sas`, { method: 'POST' }),
  // 2026-07-28: connectivity preflight + tenant bypass.
  preflightBegin: (ext) => {
    const q = ext ? `?agent_extension=${encodeURIComponent(ext)}` : '';
    return api(`/screen-recordings/preflight-begin${q}`, { method: 'POST' });
  },
  bypass: (payload = {}) =>
    api('/screen-recordings/bypass', {
      method: 'POST', body: JSON.stringify(payload),
    }),
  listBypasses: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return api(`/screen-recordings/bypasses${q ? '?' + q : ''}`);
  },
  rebindCallId: (id, callId) =>
    api(`/screen-recordings/${id}/rebind`, {
      method: 'POST', body: JSON.stringify({ call_id: callId }),
    }),
  // 2026-07-16 hardening: pull-mode fallback when the SSE rebind path
  // silently fails. Given an agent extension (+ optional started_after
  // ISO ts), returns { uniqueid, state, stale_sec } from the AMI-populated
  // agent_live_state row.
  lookupUniqueid: (ext, startedAfter) => {
    const q = new URLSearchParams({ ext });
    if (startedAfter) q.set('started_after', startedAfter);
    return api(`/screen-recordings/lookup-uniqueid?${q.toString()}`);
  },
  // 2026-07-16: coverage/health snapshot for admin badge / wallboard tile.
  health: () => api('/screen-recordings/health'),
  get:        (id) => api(`/screen-recordings/${id}`),
  getByCall:  (callId) => api(`/screen-recordings/by-call/${encodeURIComponent(callId)}`),
  // Multi-monitor (2026-09-10): ALL screens for a call, ordered by screen_index.
  getAllByCall: (callId) => api(`/screen-recordings/by-call-all/${encodeURIComponent(callId)}`),
  hasScreen:  (callIds) => api('/screen-recordings/has-screen', {
    method: 'POST', body: JSON.stringify({ call_ids: callIds }),
  }),
  streamUrl:  (id) => `/api/screen-recordings/${id}/stream`,
};

// -----------------------------------------------------------------
// QA (Quality Assurance) API — Phase QA-1 (2026-07-02)
// -----------------------------------------------------------------
export const qaApi = {
  // Forms
  listForms:   (activeOnly = true) => api(`/qa/forms?active_only=${activeOnly ? 'true' : 'false'}`),
  getForm:     (id) => api(`/qa/forms/${id}`),
  createForm:  (body) => api('/qa/forms', { method: 'POST', body: JSON.stringify(body) }),
  updateForm:  (id, body) => api(`/qa/forms/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteForm:  (id) => api(`/qa/forms/${id}`, { method: 'DELETE' }),
  // Evaluations
  createEvaluation: (body) => api('/qa/evaluations', { method: 'POST', body: JSON.stringify(body) }),
  updateEvaluation: (id, body) => api(`/qa/evaluations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  submitEvaluation: (id) => api(`/qa/evaluations/${id}/submit`, { method: 'POST' }),
  getEvaluation:    (id) => api(`/qa/evaluations/${id}`),
  listForRecording: (recId) => api(`/qa/evaluations/for-recording/${recId}`),
  listEvaluations:  (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return api(`/qa/evaluations${q ? '?' + q : ''}`);
  },
  // Reports
  agentScorecard: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return api(`/qa/reports/agent-scorecard${q ? '?' + q : ''}`);
  },

  // QA-2 assignments (2026-07-09 phase D)
  createAssignments: (body) => api('/qa/assignments', { method: 'POST', body: JSON.stringify(body) }),
  myAssignments:     (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return api(`/qa/my-assignments${q ? '?' + q : ''}`);
  },

  // 2026-07-15 QA workflow overhaul
  coached:            (id, body) => api(`/qa/evaluations/${id}/coached`,  { method: 'POST',   body: JSON.stringify(body || {}) }),
  reassign:           (id, body) => api(`/qa/evaluations/${id}/reassign`, { method: 'POST',   body: JSON.stringify(body) }),
  deleteEvaluation:   (id)       => api(`/qa/evaluations/${id}`,          { method: 'DELETE' }),
  eligibleEvaluators: ()         => api('/qa/eligible-evaluators'),

  // 2026-07-15 pm - Unassigned recordings tab in QA Manager overview
  unassignedRecordings: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return api(`/qa/unassigned-recordings${q ? '?' + q : ''}`);
  },
  quickAssign: (body) => api('/qa/quick-assign', {
    method: 'POST', body: JSON.stringify(body),
  }),
};

// Admin ops (2026-07-09 phase B)
export const adminApi = {
  failoverState:   () => api('/admin/failover/state'),
  failoverHistory: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return api(`/admin/failover/history${q ? '?' + q : ''}`);
  },
  doFailover: (body) => api('/admin/failover', { method: 'POST', body: JSON.stringify(body) }),
};
